import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BloodTestBanner from './BloodTestBanner';

const PatientForm = ({
  patientInfo = {},
  notes = '',
  onSubmit,
  onBack,
  loading = false,
  // Blood test props
  bloodTestRequired = false,
  bloodTestPrice = '0',
  bloodTestUrl = '',
  bloodTestType = 'general',
  bloodTestEmailStatus = 'pending'
}) => {
  const [formData, setFormData] = useState({
    first_name: patientInfo.first_name || '',
    last_name: patientInfo.last_name || '',
    email: patientInfo.email || '',
    phone: patientInfo.phone || '',
    notes: notes || ''
  });
  
  const [errors, setErrors] = useState({});
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Phone is optional, but validate format if provided
    if (formData.phone.trim() && !/^[0-9+\s()-]{8,20}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Create patient info object
    const patientData = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim()
    };
    
    // Call parent submit handler
    onSubmit(patientData, formData.notes);
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h6">
          Your Details
        </Typography>
      </Box>
      
      {/* Blood Test Banner - show when blood test is required */}
      {bloodTestRequired && (
        <BloodTestBanner
          status={bloodTestEmailStatus === 'pending' ? 'info' : bloodTestEmailStatus === 'sending' ? 'sending' : bloodTestEmailStatus === 'sent' ? 'success' : 'error'}
          bloodTestPrice={bloodTestPrice}
          bloodTestUrl={bloodTestUrl}
          bloodTestType={bloodTestType}
        />
      )}

      <Paper variant="outlined" sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="first_name"
                label="First Name"
                value={formData.first_name}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.first_name}
                helperText={errors.first_name}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="last_name"
                label="Last Name"
                value={formData.last_name}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.last_name}
                helperText={errors.last_name}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="phone"
                label="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone || 'Optional, but recommended for SMS reminders'}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes for your practitioner"
                value={formData.notes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                disabled={loading}
                helperText="Any additional information you'd like to share about your appointment"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Please fix the errors in the form before proceeding.
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} color="inherit" />}
                >
                  Proceed
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default PatientForm;